import { h, Component } from 'preact';
import Store from 'store/store';

import { joinClasses, joinPaths } from 'utils/utils';
import { loadPage, getPageComponent, PAGE_ERROR, PAGE_LOADING } from 'utils/pageComponents';
import { geoFromIP, setLandingLocation } from 'utils/geo';
import { getPageCanonicalLink, getPageTitle } from 'utils/metaData';
import { copy, a11yCopy } from 'utils/copy';
import { track, trackLanding, trackPageView } from 'utils/tracking';
import { getDeviceInfo } from 'utils/bolt/client';

import Header from 'components/header/header';
import Footer from 'components/footer/footer';

import s from 'base/universalHub/universalHubApp.sss';

const isServer = typeof window === 'undefined';

export default class UniversalHubApp extends Component {
	constructor(props) {
		super(props);

		this.state = {
			appState: props.initialStore,
		};

		this.setActivePageFromURL = this.setActivePageFromURL.bind(this);
		this.onClick = this.onClick.bind(this);
		this.onResize = this.onResize.bind(this);

		Store.set(props.initialStore);
		Store.on('update', () => this.setState({ appState: Store.get() }));

		if (!isServer) {
			this.setActivePageFromURL();
			this.loadPage();
			this.getUserGeo();
			trackLanding(this.getActivePageData());
		}
	}

	componentDidMount() {
		window.addEventListener('popstate', this.setActivePageFromURL);
		window.addEventListener('hashchange', this.setActivePageFromURL);
		setLandingLocation();

		window.addEventListener('resize', this.onResize);
		this.onResize();
	}

	componentWillUpdate(nextProps, nextState) {
		if (nextState.appState.activePage !== this.state.appState.activePage) {
			this.loadPage(nextState.appState.activePage);
			trackPageView(this.getActivePageData(nextState));
		}
	}

	componentWillUnmount() {
		window.removeEventListener('popstate', this.setActivePageFromURL);
		window.removeEventListener('hashchange', this.setActivePageFromURL);
		window.removeEventListener('resize', this.onResize);
	}

	onResize() {
		// To make bolt components work, we need the "client" values up to date
		Store.get().client.set(getDeviceInfo());
	}

	async getUserGeo() {
		const geo = await geoFromIP();
		Store.get().client.set('geo', geo);
		track('landing', 'location_origin', geo?.countryCode);
	}

	getActivePage(state = this.state) {
		return state.appState.activePage;
	}
	getActivePageData(state = this.state) {
		return state.appState.appData.pages?.[this.getActivePage(state)];
	}

	async loadPage(page = this.getActivePage()) {
		await loadPage(page);
		if (page === this.getActivePage()) {
			this.forceUpdate();
		}
	}

	setActivePageFromURL(e) {
		if (e?.type === 'popstate' && /^\/movies\/([^/]+)\//.test(location.pathname)) {
			location.reload();
			return;
		}

		// TODO: cache url -> id mappings ?
		const pages = Object.values(this.state.appState.appData.pages || {}).filter(p => !p.base || p.base === 'universalHub');
		let currentPath = '/' + (location.origin + location.pathname).replace(window.__BASE_URL__, '').replace(/index\.html$/, '');

		// On local/demo, using hash for movie pages, since the actual html files don't exist and we don't run the lambda function
		if (location.hash.startsWith('#/movies/')) {
			currentPath = location.hash.slice(1);
		}

		let destination;
		if (currentPath.startsWith('/movies/')) {
			const [, , slug, subPath = ''] = currentPath.split('/');
			const pageId = { '': 'movie', tickets: 'showtimes' }[subPath];
			const page = pageId && pages.find(p => p.id === pageId);
			if (page) {
				destination = { ...page, props: { slug } };
			} else {
				destination = pages.find(p => p.id === 'error-404');
			}
		} else {
			destination = pages.find(p => currentPath === p.path + (p.baseHtml && p.baseHtml !== 'index.html' ? p.baseHtml : ''));
		}

		if (destination && this.getActivePage() !== destination.id) {
			// Scroll to top ?
			window.scrollTo(0, 0);
			Store.get().set({ activePage: destination.id, activePageProps: destination.props || {} });
		}

		// Some pages might change title/canonical link even without changing (e.g. leadership when clicking on a name)
		if (destination && !destination.props?.slug) {
			const title = getPageTitle(destination);
			document.title = title;
			document.querySelector('meta[property="og:title"]')?.setAttribute('content', title);
			document.querySelector('link[rel="canonical"]')?.setAttribute('href', getPageCanonicalLink(destination));
		}
		window.dispatchEvent(new CustomEvent('powster-navigation'));
	}

	onClick(e) {
		const link = e.target.closest('a');
		if (!link || link.dataset.uncontrolled) return;
		const target = link.target || '_self';
		if (target !== '_self' || link.origin !== location.origin) return;
		// TODO: handle local film pages vs regular movie pages (both live at /movies/slug/)
		const pages = this.state.appState.appData.pages || {};
		const links = Object.fromEntries(Object.entries(pages).map(([id, { path }]) => [joinPaths(window.__BASE_URL__, path || ''), id]));
		const desired = link.origin + link.pathname;
		const destination = links[desired];
		if (destination) {
			e.preventDefault();
			history.pushState(null, '', link.href);
			this.setActivePageFromURL();
		}
	}

	skipToContent(e) {
		// We might not need this (depends on desired UX): google.com seems to do something similar, but web.dev does not
		e.preventDefault();
		e.stopPropagation();

		// TODO: should also check CSS display/visibility of ancestors
		const firstInteractiveElement = document.querySelector('#content ~ * :is(a[href], button, input, [tabindex]):not(:disabled, [tabindex="-1"])');
		firstInteractiveElement?.focus();
	}

	renderLoading() {
		return <div class={s.loading} key="loading" />;
	}

	renderError() {
		// TODO: improve (maybe external component?)
		return <div class={s.error} key="error">{copy('$ERROR_LOADING_PAGE')}</div>;
	}

	getActivePageKey() {
		const store = this.state.appState;
		return [
			store.activePage,
			store.activePageProps?.slug,
		].filter(Boolean).join('-');
	}

	renderPage() {
		if (isServer) return this.renderLoading();
		const activePage = this.getActivePage();
		const Page = getPageComponent(activePage);
		if (!Page) return;
		// TODO: improve loading/error screens (maybe external components?)
		if (Page === PAGE_LOADING) return this.renderLoading();
		if (Page === PAGE_ERROR) return this.renderError();
		return <Page key={this.getActivePageKey()} pageData={this.getActivePageData()} {...this.state.appState.activePageProps} />;
	}

	render(props, state) {
		return (
			<div class={joinClasses(s.container)} id="universalHub" onClickCapture={this.onClick}>
				<a class={s.jumpToContent} href="#content" data-uncontrolled onClick={this.skipToContent}>{a11yCopy('$SKIP_TO_CONTENT')}</a>
				<Header />
				<a id="content" />
				{this.renderPage()}
				<Footer />
			</div>
		);
	}
}
